import { Contact } from '@api/generated/graphql';
import ErrorComponent from '@components/ErrorComponent';
import { strings as errorStrings } from '@components/ErrorComponent/strings';
import {
  DrawerTable,
  DrawerTableErrorWrapper,
  DrawerTableWrapper,
} from '@pages/agent/InboxPage/components/Drawer/DrawerTable.styled';
import {
  Button,
  Form,
  Icon,
  Stack,
  TableCellProps,
  TableColumn,
} from '@servicetitan/design-system';
import { ChangeEvent, useMemo, useState } from 'react';
import type { GridPageChangeEvent } from '@progress/kendo-react-grid';
import strings from '../../strings';
import ContactMethodValueCell from '../ContactMethodValueCell';
import NotificationValueCell from '../NotificationValueCell';
import styles from './ViewAllContactsDrawerPage.module.css';

export interface ViewAllContactsDrawerPageProps {
  contacts: Contact[];
  loading: boolean;
  error: boolean;
  onEditClick: () => void;
}

const TABLE_ROW_HEIGHT = 47;
const PAGE_SIZE = 100;

interface TableState {
  skip: number;
  take: number;
}

export const ViewAllContactsDrawerPage = ({
  contacts,
  loading,
  error,
  onEditClick,
}: ViewAllContactsDrawerPageProps) => {
  const [contactSearchInput, setContactSearchInput] = useState('');
  const [tableState, setTableState] = useState<TableState>({
    skip: 0,
    take: PAGE_SIZE,
  });
  const showPagination = contacts.length > PAGE_SIZE;

  const noResults = contacts.length === 0 && !loading && !error;

  const filteredContacts = useMemo(
    () =>
      contacts.filter(
        (contact) =>
          contact.value
            ?.toLowerCase()
            .includes(contactSearchInput.toLowerCase()) ||
          contact.memo
            ?.toLowerCase()
            .includes(contactSearchInput.toLowerCase()),
      ),
    [contactSearchInput, contacts],
  );

  const onPageChange = (ev: GridPageChangeEvent) => {
    const { page } = ev;
    const { take, skip } = page;
    setTableState({ skip, take });
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setContactSearchInput(e.target.value);
    setTableState({ skip: 0, take: PAGE_SIZE });
  };

  const windowedContacts = useMemo(() => {
    return filteredContacts.slice(
      tableState.skip,
      tableState.skip + tableState.take,
    );
  }, [filteredContacts, tableState]);

  return (
    <div className={styles.viewAllContactsDrawerPage}>
      <Stack className='m-b-4'>
        <div className='flex-grow-1'>
          <Form.Input
            shortLabel={<Icon name='search' />}
            small
            placeholder={strings.placeholders.search}
            value={contactSearchInput}
            onChange={onSearchChange}
          />
        </div>
        <div>
          <Button
            className='p-x-half m-l-1'
            size='small'
            fill='subtle'
            color='primary'
            iconName='edit'
            onClick={onEditClick}
          >
            {strings.labels.editButton}
          </Button>
        </div>
      </Stack>
      <DrawerTableWrapper $flex={1} $overflowHidden>
        <DrawerTable
          $height='100%'
          onPageChange={onPageChange}
          loading={loading}
          scrollable='scrollable'
          striped={false}
          pageable={showPagination}
          data={loading ? [] : windowedContacts}
          rowHeight={TABLE_ROW_HEIGHT}
        >
          <TableColumn
            field='value'
            title={strings.labels.valueColumn}
            width='300'
            locked
            cell={(props) => <ContactMethodValueCell {...props} />}
          />
          <TableColumn field='memo' title=' ' locked />
          <TableColumn
            field='enableNotifications'
            title=' '
            locked
            width='72px'
            className='fs-3'
            cell={(props: TableCellProps) => (
              <NotificationValueCell {...props} dataItem={props.dataItem} />
            )}
          />
        </DrawerTable>
        {noResults && (
          <DrawerTableErrorWrapper>
            <ErrorComponent
              image={'block'}
              title={errorStrings.noResultsHeadline}
              body={errorStrings.tryAnotherSearch}
            />
          </DrawerTableErrorWrapper>
        )}
      </DrawerTableWrapper>
    </div>
  );
};
